import { Column, ColumnDef } from '@tanstack/react-table'
import { CheckIcon, ChevronDown } from 'lucide-react'
import { DropdownMenu } from 'radix-ui'
import * as React from 'react'
import styled from 'styled-components'

const StyledDropdownTrigger = styled(DropdownMenu.Trigger)`
  min-height: 2rem;
  border: none;
  display: flex;
  justify-content: space-between;
  color: ${({ theme }) => theme.forecast.secondaryText};
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 0.25rem;
  align-items: center;
  font-weight: 600;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.forecast.hoverButton} !important;
  }
  & > svg {
    min-width: 1.5rem;
    padding-left: 0.25rem;
  }
`

const StyledCheckboxItem = styled(DropdownMenu.CheckboxItem)`
  border: none;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  align-items: center;
  padding: 0.25rem;
  border-radius: 0.25rem !important;

  &:hover {
    cursor: pointer;
    background-color: ${({ theme }) => theme.forecast.hoverButton};
  }
`

const StyledDropdownContent = styled(DropdownMenu.Content)`
  margin-top: 0.25rem;
  border-radius: 0.25rem !important;
  background-color: ${({ theme }) => theme.forecast.glassBackground};
  backdrop-filter: blur(2rem);
  box-shadow:
    0rem 0.0625rem 0.125rem 0rem ${({ theme }) => theme.forecast.boxShadow},
    0rem 0.25rem 0.5rem 0rem ${({ theme }) => theme.forecast.boxShadow};
  -webkit-backdrop-filter: blur(2rem);
`

const StyledColumnsSpan = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 600;
`

export const ColumnConfiguration = <TData,>({
  columns,
  getColumn,
  defaultVisibleColumnIds,
}: {
  columns: ColumnDef<TData>[]
  getColumn: (columnID: string) => Column<TData, unknown> | undefined
  defaultVisibleColumnIds?: string[]
}) => {
  return (
    <DropdownMenu.Root>
      <StyledDropdownTrigger>
        <StyledColumnsSpan>Columns</StyledColumnsSpan>
        <ChevronDown size={15} />
      </StyledDropdownTrigger>

      <DropdownMenu.Portal>
        <StyledDropdownContent align='start'>
          {columns.map((column, i) => {
            const col = getColumn(column.id || '')
            if (col !== undefined) {
              const isDefaultVisible = defaultVisibleColumnIds
                ? defaultVisibleColumnIds.includes(column.id || '')
                : col.getIsVisible()
              return (
                <StyledCheckboxItem
                  key={i}
                  checked={col.getIsVisible() || isDefaultVisible}
                  onCheckedChange={() => col?.toggleVisibility()}>
                  <span>{String(column.header)}</span>
                  <DropdownMenu.ItemIndicator>
                    <CheckIcon size={15} />
                  </DropdownMenu.ItemIndicator>
                </StyledCheckboxItem>
              )
            }
          })}
        </StyledDropdownContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
